<script>
import base from '@/views/config/addon-cards/templates/base.vue';
import { ADDON_GROUP, planTypes } from '@/common/constants';
import addonCustomSelect from '@/plans/mx/addonCustomSelect';
import { get, orderBy } from 'lodash';
import { plansof } from '@/utils/chain';
import { computedPbxCallingPlans } from '@/common/service/computers/plans/pbx';
import { addDefaultPbxBasePlanToShoppingCartByQuery } from '@/flows/BUY_NOW';
import { mapGetters, mapMutations } from 'vuex';
import mxSoftBundleCustomMixin from './mixin/soft-bundle-pbx';
import computedMixin from './mixin/computed';
import { forCurrentPlanShowInAddonForSoftBundle } from '@/views/config/soft-bundle/helper';
import { isBizPlusContext } from '../ZOOM_MEETING/common/utils';
import { canZoomPhoneAsAddonPlan, getPbxRegionCodeByIpRegion } from './helper/utils';
import { AddOnGroupTrackingUILocation, getTrackingFlowType } from '@/aspects/trackings';
import { translateToEn } from '@/plugins/i18n';
import { PBX_REGIONS } from './helper/constants';

export default {
  name: 'ZoomPhoneAddon',
  mixins: [base, addonCustomSelect, mxSoftBundleCustomMixin, computedMixin],
  for: () => {
    if (forCurrentPlanShowInAddonForSoftBundle(planTypes.ZOOM_PHONE)) {
      return true;
    }
    return canZoomPhoneAsAddonPlan();
  },
  group() {
    if (forCurrentPlanShowInAddonForSoftBundle(planTypes.ZOOM_PHONE) || !isBizPlusContext) {
      return [ADDON_GROUP.WIN_BACK, ADDON_GROUP.DEFAULT];
    }
    return [ADDON_GROUP.BIZ_PLUS];
  },
  data() {
    return {
      title: this.$t('billing.pbx.title'),
      trackingPlanName: translateToEn('billing.pbx.title'),
      logo: require('@/assets/plans/logo_pbx.svg'),
      planType: planTypes.ZOOM_PHONE,
      description: this.$t('billing.pbx.desc'),
    };
  },
  computed: {
    ...mapGetters(['selectedPbxBasePlan', 'getSelectedPlans', 'ipLocation']),
    forPbxAddonWithSoftBundle() {
      return forCurrentPlanShowInAddonForSoftBundle(planTypes.ZOOM_PHONE);
    },
    pbxCallingPlans() {
      return computedPbxCallingPlans(this.planMapFlat);
    },
    currentIpPbxRegionCode() {
      return getPbxRegionCodeByIpRegion(get(this.ipLocation, 'cn'));
    },
    currentProductPlans() {
      // winback logic
      if (this.pastPlans?.length) {
        const { planCode } = this.pastPlans?.find(p => planTypes[p.semanticType] === planTypes.ZOOM_PHONE) || {};
        if (planCode) {
          const { region } = plansof(this.planMapFlat).code(planCode).get(0) || {};
          return this.getDefaultMonthlyCallingPlanListByRegion(region);
        }
      }

      // default logic with soft bundle
      if (this.forPbxAddonWithSoftBundle) {
        return this.getDefaultMonthlyCallingPlanListByRegion(this.soft_bundle_pbxCallingRegion);
      }

      return this.getDefaultMonthlyCallingPlanListByRegion(this.currentIpPbxRegionCode);
    },
    lowestPlan() {
      return get(this.currentProductPlans, [0], {});
    },
  },
  methods: {
    ...mapMutations(['setPbxBasePlanCode', 'setPlanMapFlat']),
    getGlobalPhonePlans() {
      const GlobalCallingPlansCategory = ['ZSU', 'ZSM'];
      return plansof(this.pbxCallingPlans).category(...GlobalCallingPlansCategory).get();
    },
    getDefaultMonthlyCallingPlanListByRegion(region) {
      let list = [];
      if (region === PBX_REGIONS.GLOBAL) {
        list = this.getGlobalPhonePlans();
      } else {
        list = plansof(this.pbxCallingPlans).monthly().region(region).get();
      }
      return orderBy(list, ['amount'], 'asc');
    },
    getAddDefaultPlan(regionCode) {
      if (regionCode === PBX_REGIONS.GLOBAL) {
        const plans = this.getGlobalPhonePlans();
        return plansof(plans).cycle(this.pbxBillingCycle).get(0);
      }
      return plansof(this.pbxCallingPlans).cycle(this.pbxBillingCycle).region(regionCode).get(0);
    },
    addOn(evt) {
      // addPlanFromEnterKey For A11Y requirement
      // target is BUTTON comes from keyboard enter, will auto focaus on the first option in config card
      // target is SPAN comes from click
      const addPlanFromEnterKey = get(evt?.target, 'tagName', '') == 'BUTTON';
      if (addPlanFromEnterKey) {
        document.addPlanFromEnterKey = true;
      }

      let addPlan = this.formPlan;

      // soft bundle logic
      if (this.forPbxAddonWithSoftBundle) {
        addPlan = this.getAddDefaultPlan(this.soft_bundle_pbxCallingRegion);
      }
      // Add Zoom Phone by ip region
      else if (
        this.group === ADDON_GROUP.BIZ_PLUS ||
        this.group === ADDON_GROUP.DEFAULT
      ) {
        addPlan = this.getAddDefaultPlan(this.currentIpPbxRegionCode);
      }

      this.updateSelectPlan({
        ...addPlan,
        cartInfo: {
          selected_number: this.form.selectedNumber,
        },
      });

      this.$emit('add', this.$options?.key);
      //add phone usage plan
      addDefaultPbxBasePlanToShoppingCartByQuery(this.planMapFlat);
      this.setPlanMapFlat([...this.planMapFlat]);
      if (this.selectedPbxBasePlan) {
        this.setPbxBasePlanCode(this.selectedPbxBasePlan.plan_code);
        this.savePersist()
      }
      this.$tracking({
        event: 'buyInteraction',
        buyInteraction: `${this.trackingBasePlanName} add-ons`,
        buySelection: `${this.trackingPlanName} - ${this.trackingAddAction}`,
        uiLocation: AddOnGroupTrackingUILocation[this.group],
        flowType: getTrackingFlowType()
      })
      this.$nextTick(this.focusToConfig);
    },
  }
};
</script>
